import {
  Box,
  CheckboxField,
  ChevronDownIcon,
  ChevronUpIcon,
  Flex,
  List,
} from '@iheartjane/reefer';
import { useCallback, useState } from 'react';

import { FilterCheckBox } from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors/filterSelector/filterCheckBox';
import {
  useProductSearchFilters,
  useSearchParamFilter,
} from '@jane/bloom/hooks/filters';
import { FilterCategoryLabel } from '@jane/bloom/hooks/filters/useProductSearchFilters';

import { FilterActions } from './filterActions';

interface FilterCategoriesButtonProps {
  availableProductsCount?: number;
  /** Injected by FilterButtonWrapper */
  closePopover?: () => void;
}

const filterKey = 'categories';

export const FilterCategoriesButton = ({
  availableProductsCount,
  closePopover,
}: FilterCategoriesButtonProps) => {
  const { clearFilter, getFilterStatus, setFilter } =
    useSearchParamFilter('categories');
  const {
    getProductSearchFilterOptions,
    getProductSubcategories,
    productSearchFilters,
    selectedCategory,
  } = useProductSearchFilters();
  const [subCatsShown, setSubCatsShown] = useState<string[]>([]);

  const isSubCatShown = useCallback(
    (category: string) => subCatsShown.includes(category),
    [subCatsShown]
  );

  const getSubCats = useCallback(
    (category: string) =>
      productSearchFilters?.categories?.find((c) => c.value === category)
        ?.subCategories || [],
    [productSearchFilters?.categories]
  );

  const hasSubCats = useCallback(
    (category: string) => getSubCats(category).length > 0,
    [getSubCats]
  );

  const showSubCat = useCallback(
    (category: string) => {
      if (!isSubCatShown(category)) {
        setSubCatsShown([...subCatsShown, category]);
      }
    },
    [isSubCatShown, subCatsShown, setSubCatsShown]
  );

  const hideSubCat = useCallback(
    (category: string) => {
      if (isSubCatShown(category)) {
        setSubCatsShown(subCatsShown.filter((cat) => cat !== category));
      }
    },
    [isSubCatShown, subCatsShown, setSubCatsShown]
  );

  const filterOptions = selectedCategory
    ? getProductSubcategories(selectedCategory)
    : getProductSearchFilterOptions(filterKey);

  return filterOptions.length > 0 ? (
    <Box px={16} pt={16} data-testid="product-filter-checklist-categories">
      <List label={FilterCategoryLabel[filterKey]} mb={16}>
        {filterOptions.map((category) => {
          const isChecked = getFilterStatus(category.value) === 'selected';
          return (
            <List.Item key={`categories/${category.value}`}>
              <Flex>
                <CheckboxField
                  disabled={category.count === 0}
                  width="100%"
                  label={`${category.label} (${category.count})`}
                  name={category.value}
                  indeterminate={
                    getFilterStatus(category.value) === 'children-selected'
                  }
                  checked={isChecked}
                  onChange={(checked) => {
                    if (checked !== isChecked) {
                      setFilter(category.value, checked);
                    }
                  }}
                >
                  {isSubCatShown(category.value) &&
                    hasSubCats(category.value) && (
                      <List
                        label={category.label}
                        key={`categories${category.value}`}
                        ml={16}
                        mt={8}
                      >
                        {getSubCats(category.value).map((sub) => (
                          <FilterCheckBox
                            filterKey={filterKey}
                            filter={sub}
                            key={`${category.value}${sub.value}`}
                            label={`${sub.label} (${sub.count})`}
                          />
                        ))}
                      </List>
                    )}
                </CheckboxField>
                {hasSubCats(category.value) &&
                  isSubCatShown(category.value) && (
                    <ChevronUpIcon
                      altText="Hide Subcategories"
                      onClick={() => hideSubCat(category.value)}
                    />
                  )}
                {hasSubCats(category.value) &&
                  !isSubCatShown(category.value) && (
                    <ChevronDownIcon
                      altText="Show Subcategories"
                      onClick={() => showSubCat(category.value)}
                    />
                  )}
              </Flex>
            </List.Item>
          );
        })}
      </List>
      <FilterActions
        availableProductsCount={availableProductsCount}
        handleClearAll={clearFilter}
        handleClose={closePopover}
        sticky
      />
    </Box>
  ) : null;
};
